import React, { useEffect, useState } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { add, pencil } from 'ionicons/icons';
import { useHistory } from 'react-router';
import { GearCard } from './GearCard';
import { api } from '@/utils/api';
import { t } from 'i18next';
import HorizontalScrollMedia from './HorizontalScrollMedia';

interface GolfClub {
  id: number;
  type: string;
  brandName?: string;
  modelName?: string;
  customModelName?: string;
  clubNumber?: number;
  loft?: number;
  inBag: boolean;
  performance?: {
    averageDistance?: number;
  };
}

interface UserBagProps {
  userId?: string;
  limit?: number;
  showEditButton?: boolean;
}

const UserBag: React.FC<UserBagProps> = ({
  userId,
  limit = 4,
  showEditButton = true
}) => {
  const [clubs, setClubs] = useState<GolfClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();

  const fetchClubs = async () => {
    try {
      setLoading(true);

      // If userId is provided, fetch that user's clubs, otherwise fetch current user's clubs
      const endpoint = userId
        ? `/golf-gear/users/${userId}/clubs?inBag=true`
        : '/golf-gear/clubs?inBag=true';

      const response = await api.get(endpoint);

      if (!response.data) {
        throw new Error('Failed to fetch clubs');
      }

      setClubs(response.data);
    } catch (err) {
      console.error('Error fetching clubs:', err);
      setError('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, [userId]);

  // Sort clubs by type and number
  const sortedClubs = [...clubs].sort((a, b) => {
    const typeOrder = {
      driver: 1,
      wood: 2,
      hybrid: 3,
      iron: 4,
      wedge: 5,
      putter: 6
    };

    // First sort by type
    const typeComparison = (typeOrder[a.type as keyof typeof typeOrder] || 99) -
      (typeOrder[b.type as keyof typeof typeOrder] || 99);

    if (typeComparison !== 0) return typeComparison;

    // Then by club number (if available)
    if (a.clubNumber && b.clubNumber) {
      return a.clubNumber - b.clubNumber;
    }

    // If no club numbers, maintain original order
    return 0;
  });

  const displayClubs = limit ? sortedClubs.slice(0, limit) : sortedClubs;

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <IonSpinner name="circular" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (clubs.length === 0) {
    return (
      <div className="p-4 bg-secondary rounded-lg text-center">
        <p className="mb-2">Aucun club dans le sac</p>
        {showEditButton && (
          <IonButton size="small" onClick={() => history.push('/my-bag/edit')}>
            Configurer le sac
            <IonIcon slot="end" icon={add} />
          </IonButton>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{t('mybag')}</h3>
        {showEditButton && (
          <IonButton size="small" fill="clear" onClick={() => history.push('/my-bag')}>
            <IonIcon slot="icon-only" icon={pencil} />
          </IonButton>
        )}
      </div>

      <HorizontalScrollMedia className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {displayClubs.map((club) => (
          <GearCard 
            key={club.id} 
            club={club} 
            onClick={() => history.push(`/my-bag/edit?clubId=${club.id}`)}
            showDistance={false}
          />
        ))}
      </HorizontalScrollMedia>

      {clubs.length > limit && showEditButton && (
        <div className="text-center">
          <IonButton size="small" fill="clear" onClick={() => history.push('/my-bag')}>
            Voir tous les clubs ({clubs.length})
          </IonButton>
        </div>
      )}
    </div>
  );
};

export default UserBag;
